import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { AlertTriangle, RefreshCw } from "react-feather";
import { toast } from "react-toastify";
import {
  getTenantSubscriptionsAPI,
  terminateSubscriptionAPI,
  updateSubscriptionAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import DrawerPanel from "../../../Components/shared/DrawerPanel";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./TenantSubscriptions.module.scss";

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    active: styles.statusActive,
    trial: styles.statusTrial,
    past_due: styles.statusPastDue,
    cancelled: styles.statusCancelled,
  };
  return (
    <span className={map[status] || styles.statusCancelled}>
      {status === "past_due" ? "Past Due" : status}
    </span>
  );
}

// ─── Plan Badge ───────────────────────────────────────────────────────────────
function PlanBadge({ plan }) {
  return <span className={styles.planBadge}>{plan}</span>;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  );
}

// ─── Filter Select ────────────────────────────────────────────────────────────
function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className={styles.filterGroup}>
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([val, text]) => (
          <option key={val} value={val}>
            {text}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TenantSubscriptions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [commercial, setCommercial] = useState({});
  const [termOpen, setTermOpen] = useState(false);
  const [termReason, setTermReason] = useState("");
  const [fPlan, setFPlan] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fCycle, setFCycle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTenantSubscriptionsAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let r = rows;
    if (fPlan) r = r.filter((x) => x.planName === fPlan);
    if (fStatus) r = r.filter((x) => x.status === fStatus);
    if (fCycle) r = r.filter((x) => x.billingCycle === fCycle);
    return r;
  }, [rows, fPlan, fStatus, fCycle]);

  // ─── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === "active").length;
    const totalArr = rows.reduce((s, r) => s + Number(r.amountUsd || 0), 0);
    const expiringSoon = rows.filter((r) => {
      const d = new Date(r.renewalDate || r.currentPeriodEnd).getTime();
      return (
        d - Date.now() < 45 * 86400000 &&
        d > Date.now() &&
        r.status === "active"
      );
    }).length;
    return { total: rows.length, active, totalArr, expiringSoon };
  }, [rows]);

  const openDrawer = (row) => {
    setDrawer(row);
    setCommercial(row.commercialTerms || {});
  };

  const saveCommercial = async () => {
    if (!drawer) return;
    try {
      await updateSubscriptionAPI(drawer.id, { commercialTerms: commercial });
      toast.success("Commercial terms saved");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ─── Table columns ─────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        name: "Tenant",
        selector: (r) => r.companyName,
        sortable: true,
        wrap: true,
        cell: (r) => (
          <span
            style={{ fontWeight: 600, color: "#1a1525", fontSize: "0.85rem" }}
          >
            {r.companyName}
          </span>
        ),
      },
      {
        name: "Plan",
        selector: (r) => r.planName,
        width: "110px",
        cell: (r) => <PlanBadge plan={r.planName} />,
      },
      { name: "Contract", selector: (r) => r.contractTerm, width: "120px" },
      { name: "Cycle", selector: (r) => r.billingCycle, width: "100px" },
      {
        name: "Amount",
        selector: (r) => r.amountUsd,
        width: "100px",
        sortable: true,
        cell: (r) => (
          <span style={{ fontWeight: 600, color: "#644998" }}>
            ${Number(r.amountUsd).toLocaleString()}
          </span>
        ),
      },
      {
        name: "Renewal",
        selector: (r) => r.renewalDate || r.currentPeriodEnd,
        width: "130px",
        sortable: true,
        cell: (r) => {
          const d = new Date(r.renewalDate || r.currentPeriodEnd);
          const soon =
            d.getTime() - Date.now() < 45 * 86400000 &&
            d.getTime() > Date.now() &&
            r.status === "active";
          return (
            <span
              style={{
                color: soon ? "#b45309" : "inherit",
                fontWeight: soon ? 600 : 400,
              }}
            >
              {d.toLocaleDateString()}
            </span>
          );
        },
      },
      {
        name: "Status",
        selector: (r) => r.status,
        width: "120px",
        cell: (r) => <StatusBadge status={r.status} />,
      },
    ],
    [],
  );

  const rowStyles = useMemo(
    () => [
      {
        when: (row) => {
          const d = new Date(row.renewalDate || row.currentPeriodEnd).getTime();
          return (
            d - Date.now() < 45 * 86400000 &&
            d > Date.now() &&
            row.status === "active"
          );
        },
        style: { backgroundColor: "rgba(247, 168, 0, 0.06)" },
      },
    ],
    [],
  );

  // ─── Loading State ──────────────────────────────────────────────────────────
  if (loading && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Tenant Subscriptions"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Subscriptions", path: platformUrl("subscriptions") },
          ]}
        />
        <div className={styles.statsRow}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={styles.skeletonCard}>
              {[1, 2, 3].map((j) => (
                <div key={j} className={styles.skeletonRow} />
              ))}
            </div>
          ))}
        </div>
        <div className={styles.skeletonCard}>
          {[1, 2, 3, 4, 5].map((j) => (
            <div key={j} className={styles.skeletonRow} />
          ))}
        </div>
      </div>
    );
  }

  // ─── Error State ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Tenant Subscriptions"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Subscriptions", path: platformUrl("subscriptions") },
          ]}
        />
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <AlertTriangle size={22} />
          </div>
          <h3>Could not load subscriptions</h3>
          <p>{error.message}</p>
          <button className={styles.btnPrimary} onClick={load}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── Main Render ─────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title="Tenant Subscriptions"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Subscriptions", path: platformUrl("subscriptions") },
        ]}
      />

      {/* Stats Row */}
      <div className={styles.statsRow}>
        <StatCard
          label="Total Tenants"
          value={stats.total}
          sub="across all plans"
        />
        <StatCard label="Active" value={stats.active} sub="subscriptions" />
        <StatCard
          label="Total ARR"
          value={`$${stats.totalArr.toLocaleString()}`}
          sub="annualized"
        />
        <StatCard
          label="Renewing Soon"
          value={stats.expiringSoon}
          sub="within 45 days"
        />
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <FilterSelect
          label="Plan"
          value={fPlan}
          onChange={setFPlan}
          options={[
            ["", "All plans"],
            ["Starter", "Starter"],
            ["Growth", "Growth"],
            ["Enterprise", "Enterprise"],
            ["Custom", "Custom"],
          ]}
        />
        <FilterSelect
          label="Status"
          value={fStatus}
          onChange={setFStatus}
          options={[
            ["", "All statuses"],
            ["active", "Active"],
            ["past_due", "Past Due"],
            ["trial", "Trial"],
            ["cancelled", "Cancelled"],
          ]}
        />
        <FilterSelect
          label="Billing Cycle"
          value={fCycle}
          onChange={setFCycle}
          options={[
            ["", "All cycles"],
            ["monthly", "Monthly"],
            ["annual", "Annual"],
          ]}
        />
      </div>

      {/* Data Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <h2>Subscriptions</h2>
          <span className={styles.tableCount}>{filtered.length} results</span>
        </div>
        <DataTableWrapper
          columns={columns}
          data={filtered}
          loading={false}
          onRowClick={openDrawer}
          customTableProps={{ conditionalRowStyles: rowStyles }}
        />
      </div>

      {/* Drawer */}
      <DrawerPanel
        isOpen={Boolean(drawer)}
        onClose={() => setDrawer(null)}
        title={drawer ? drawer.companyName : ""}
        width={440}
      >
        {drawer && (
          <div className={styles.drawer}>
            <div className={styles.drawerSection}>
              <h6>Commercial Terms</h6>
              <div className={styles.drawerField}>
                <label>PO Number</label>
                <input
                  value={commercial.poNumber || ""}
                  onChange={(e) =>
                    setCommercial((c) => ({ ...c, poNumber: e.target.value }))
                  }
                  placeholder="e.g. PO-2024-0042"
                />
              </div>
              <div className={styles.drawerField}>
                <label>Invoice Contact Email</label>
                <input
                  type="email"
                  value={commercial.invoiceContactEmail || ""}
                  onChange={(e) =>
                    setCommercial((c) => ({
                      ...c,
                      invoiceContactEmail: e.target.value,
                    }))
                  }
                  placeholder="billing@company.com"
                />
              </div>
              <div className={styles.drawerField}>
                <label>Discount %</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={commercial.discountPct ?? 0}
                  onChange={(e) =>
                    setCommercial((c) => ({
                      ...c,
                      discountPct: +e.target.value,
                    }))
                  }
                />
              </div>
              <div className={styles.drawerField}>
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={commercial.notes || ""}
                  onChange={(e) =>
                    setCommercial((c) => ({ ...c, notes: e.target.value }))
                  }
                  placeholder="Internal notes about this account…"
                />
              </div>
            </div>

            <div className={styles.drawerActions}>
              <button className={styles.btnPrimary} onClick={saveCommercial}>
                Save Commercial Terms
              </button>

              <Dropdown>
                <Dropdown.Toggle id="chg-plan">Change Plan</Dropdown.Toggle>
                <Dropdown.Menu>
                  {["Starter", "Growth", "Enterprise"].map((p) => (
                    <Dropdown.Item
                      key={p}
                      onClick={async () => {
                        await updateSubscriptionAPI(drawer.id, { planName: p });
                        toast.success(`Plan changed to ${p}`);
                        load();
                      }}
                    >
                      {p}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <button
                className={styles.btnDanger}
                onClick={() => setTermOpen(true)}
              >
                Terminate
              </button>
            </div>
          </div>
        )}
      </DrawerPanel>

      {/* Terminate Confirm Modal */}
      <ConfirmModal
        isOpen={termOpen}
        onClose={() => setTermOpen(false)}
        onConfirm={async () => {
          try {
            await terminateSubscriptionAPI(drawer?.id, termReason);
            toast.success("Subscription terminated");
            setTermOpen(false);
            setDrawer(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Terminate subscription?"
        message={
          <div>
            <p
              style={{
                color: "#6b6278",
                fontSize: "0.87rem",
                marginBottom: "0.75rem",
              }}
            >
              This cannot be undone without sales intervention. Please provide a
              reason below.
            </p>
            <input
              style={{
                width: "100%",
                border: "1px solid rgba(100,73,152,0.2)",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                fontSize: "0.85rem",
                boxSizing: "border-box",
              }}
              placeholder="Reason for termination (required)"
              value={termReason}
              onChange={(e) => setTermReason(e.target.value)}
            />
          </div>
        }
        confirmLabel="Terminate"
        danger
      />
    </div>
  );
}
